import type { WorkflowExecutionFailedType } from '../generated/enums';
import type { SelectorStrategiesDTO } from './selector-strategy.dto';
import { z } from 'zod/v3';

// ========== Zod Schemas for Runtime Validation ==========
// These schemas mirror the TypeScript interfaces below and are used for:
// 1. LLM structured output validation (compile-workflow-from-doc.ts)
// 2. Database storage validation (TemplateExecutionConfig)
// 3. API request/response validation

/**
 * Workflow Parameter Schema
 * Defines a variable parameter that can be passed to a workflow
 */
export const WorkflowParamSchema = z.object({
  type: z.enum(['string', 'number', 'ordinal', 'boolean', 'url', 'email', 'date']),
  value: z.unknown(),
  description: z.string(),
  required: z.boolean().default(true),
});

/**
 * Step Extraction Schema
 * Defines what data to extract from a page element
 */
export const StepExtractionSchema = z.object({
  name: z.string().describe('Output variable name (e.g., "issueTitle", "os")'),
  selector: z.string().optional().describe('Specific selector for this extraction (overrides step hints)'),
  xpath: z.string().optional(),
  extractField: z.enum(['textContent', 'value', 'href', 'src', 'innerText', 'innerHTML', 'attribute']).optional(),
  description: z.string().optional().describe('What this extraction represents'),
});

/**
 * Step Hints Schema
 * Provides selector strategies and additional context for step execution
 */
export const StepHintsSchema = z.object({
  // Selector strategies
  selector: z.string().optional(),
  xpath: z.string().optional(),
  testId: z.string().optional(),
  ariaLabel: z.string().optional(),

  // Element identification (CRITICAL for Fast Path validation)
  domText: z.string().optional().describe('CRITICAL: Expected text content for validation. Use {{param}} for parameterized values. Required for click/type actions with parameters.'),
  ordinality: z.string().optional().describe('CRITICAL: Position in list (first, last, 2, {{position}}). Required for selecting items in lists.'),
  containerSelector: z.string().optional().describe('Container to constrain search'),

  // Validation
  fingerprint: z.object({
    id: z.string().optional(),
    href: z.string().optional(),
    textContent: z.string().optional(),
    tagName: z.string().optional(),
  }).optional(),

  // Navigation hints
  url: z.string().optional(),
  urlPattern: z.string().optional(),

  // Type hints
  value: z.string().optional(),
  clearFirst: z.boolean().optional(),

  // Extract hints
  extractField: z.enum(['textContent', 'value', 'href', 'src', 'innerText']).optional(),

  // Wait hints
  waitFor: z.object({
    type: z.enum(['url', 'element', 'text', 'networkIdle']),
    value: z.string().optional(),
    timeout: z.number().optional(),
  }).optional(),
});

/**
 * Observed State Schema
 * Signals captured during recording for validation
 */
export const ObservedStateSchema = z.object({
  errorElements: z.array(z.object({
    text: z.string(),
    selector: z.string(),
    color: z.string().optional(),
    role: z.string().optional(),
  })).optional(),
  urlChanged: z.boolean().optional(),
  modalAppeared: z.boolean().optional(),
  httpStatus: z.number().optional(),
});

/**
 * Step Validation Policy Schema
 * Optional contract to guide step success/failure validation.
 * Backward-compatible: when omitted, runtime derives policy from action + intent.
 */
export const StepValidationPolicySchema = z.object({
  mode: z.enum(['auto', 'dom', 'visual', 'hybrid']).optional(),
  intentType: z
    .enum([
      'focus_input',
      'open_panel',
      'close_panel',
      'toggle_on',
      'toggle_off',
      'submit_form',
      'navigate',
      'observe_only',
      'input_value',
      'select_option',
      'generic_click',
    ])
    .optional(),
  authFlow: z.enum(['none', 'login', 'logout', 'switch_user', 'unknown']).optional(),
  sessionExpiryCheck: z.enum(['auto', 'strict', 'off']).optional(),
  authFlowSource: z.enum(['llm', 'heuristic', 'manual']).optional(),
  authFlowConfidence: z.number().min(0).max(1).optional(),
  requiredSignals: z.array(z.string()).optional(),
  optionalSignals: z.array(z.string()).optional(),
  failureSignals: z.array(z.string()).optional(),
  strictness: z.enum(['strict', 'balanced', 'lenient']).optional(),
});

/**
 * Workflow Step Schema
 * Complete definition of an executable workflow step
 */
export const WorkflowStepSchema = z.object({
  id: z.string(),
  intent: z.string().describe('What this step accomplishes'),
  action: z.enum(['navigate', 'click', 'type', 'extract', 'wait', 'scroll', 'hover', 'select']),
  prompt: z.string().describe('Natural language instruction with @param references'),
  hints: StepHintsSchema,
  output: z.string().optional().describe('DEPRECATED: Use extractions array instead. Single output variable name'),

  // Multi-value extraction support
  extractions: z.array(StepExtractionSchema).optional().describe('Multiple values to extract from this step. Use this instead of output for extract actions.'),

  // Natural language success criteria
  successCriteria: z.string().optional().describe('Natural language description of expected outcome (e.g., "login should fail", "extract the title")'),

  // Observed state from recording
  observedState: ObservedStateSchema.optional().describe('Signals captured during recording'),

  // Optional validation policy (intent + signal contract)
  validation: StepValidationPolicySchema.optional().describe('Optional step-level validation policy'),
});

/**
 * Workflow Param Entry Schema
 * Key-value pair representation for array form (Gemini-compatible)
 */
export const WorkflowParamEntrySchema = z.object({
  name: z.string().describe('Parameter name (without @ prefix)'),
  param: WorkflowParamSchema,
});

/**
 * Workflow Output Schema
 * Expected outputs from workflow execution
 */
export const WorkflowOutputSchema = z.object({
  name: z.string().describe('Output variable name'),
  type: z.enum(['string', 'number', 'boolean', 'object', 'array']),
  required: z.boolean().default(false),
  description: z.string(),
  extractedFrom: z.string().optional().describe('Step ID that produces this output'),
});

/**
 * Compilation Result Schema
 * Complete workflow structure returned by LLM compilation
 */
export const CompilationResultSchema = z.object({
  confidence: z.number().min(0).max(1).describe('Confidence score 0.0-1.0'),
  summary: z.string().optional().describe('2-3 sentence summary'),
  params: z.array(WorkflowParamEntrySchema).optional().describe('Variable parameters with @param syntax'),
  steps: z.array(WorkflowStepSchema).describe('Executable workflow steps'),

  // Workflow-level success criteria
  outputs: z.array(WorkflowOutputSchema).optional().describe('Expected outputs from workflow execution'),
  successCriteria: z.string().describe('REQUIRED: Natural language description of workflow success, based on the last step content'),
});

// ========== TypeScript Interfaces ==========

/**
 * Workflow DTOs for DeepGuide Agent Reliability Layer
 * Based on workflow-language-spec.md
 *
 * These DTOs define the standardized JSON format for workflows that can be:
 * 1. Compiled from LLM (from documentation)
 * 2. Edited visually using ReactFlow
 * 3. Executed by the DeepGuide execution engine
 */

// ========== Core Workflow Types ==========

export interface WorkflowDTO {
  id: string;
  docId?: string;
  workspaceId: string;
  name: string;
  description?: string;
  version: number;

  params: Record<string, WorkflowParamDTO>;
  steps: WorkflowStepDTO[];
  documentation?: WorkflowDocumentationDTO;
  metadata: WorkflowMetadataDTO;

  // Workflow-level success criteria (NEW)
  outputs?: WorkflowOutputDTO[];
  successCriteria?: string; // Natural language description

  createdAt: string;
  updatedAt: string;
  compiledBy?: 'llm' | 'human' | 'hybrid';
}

export interface WorkflowOutputDTO {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'object' | 'array';
  required: boolean;
  description: string;
  extractedFrom?: string; // Step ID
}

export interface WorkflowParamDTO {
  type: ParamType;
  value: any;
  description: string;
  required?: boolean;
  validation?: ParamValidationDTO;
}

export type ParamType =
  | 'string'
  | 'number'
  | 'ordinal'
  | 'boolean'
  | 'url'
  | 'email'
  | 'date';

export interface ParamValidationDTO {
  pattern?: string; // Regex pattern
  min?: number; // Min value (for numbers)
  max?: number; // Max value (for numbers)
  enum?: string[]; // Allowed values
}

export interface WorkflowMetadataDTO {
  // Compilation info
  compiledAt: string;
  compiledBy: string; // "gpt-4o" | "claude-3.5-sonnet" | "human"
  compilerVersion: string; // "1.0.0"
  schemaVersion: number; // Workflow schema version

  // Quality metrics
  confidence?: number; // 0.0-1.0
  accuracy?: number; // 0.0-1.0
  editedSteps?: number;

  // Source info
  sourceDocId?: string;
  sourceDocVersion?: number; // Doc version at compilation
  sourceDocHash?: string; // SHA-256 hash
  sourceStepCount: number;

  // Usage stats
  executionCount?: number;
  successRate?: number;
  avgCost?: number;
  fastPathRate?: number;

  // Learning
  adaptationCount?: number;
  lastAdaptedAt?: string;
  adaptationSuccessRate?: number;
}

// ========== Workflow Documentation ==========

export interface WorkflowDocumentationDTO {
  markdown: string; // Complete documentation in markdown
  title: string;
  summary?: string;
  tags?: string[];
  stepDocs?: StepDocumentationDTO[];

  sourceDocId?: string;
  sourceUrl?: string;

  generatedAt?: string;
  generatedBy?: string;
}

export interface StepDocumentationDTO {
  stepId: string;

  title: string;
  description?: string;

  guideAction?: string;
  intent?: string;

  screenshot?: string;
  screenshotCaption?: string;

  tips?: string[];
  warnings?: string[];

  recordedAt?: string;
  url?: string;
}

// ========== Workflow Steps ==========

export interface WorkflowStepDTO {
  id: string;
  intent: string;
  action: StepAction;
  prompt: string;
  hints: StepHintsDTO;
  output?: string; // Legacy: single output name (deprecated, use extractions)
  extractions?: StepExtractionDTO[]; // NEW: Multiple extractions per step
  condition?: StepConditionDTO;
  retry?: RetryConfigDTO;
  metadata?: Record<string, any>;

  // Natural language success criteria
  successCriteria?: string; // e.g., "login should fail", "extract the title"

  // Recording signals
  observedState?: ObservedStateDTO;

  // Optional validation policy (intent + signal contract)
  validation?: StepValidationPolicyDTO;

  // Pagination configuration (RFC-103)
  pagination?: StepPaginationConfigDTO;

  // Selector strategies from recording (for Fast Path execution)
  selectorStrategies?: SelectorStrategiesDTO;
}

// ========== Pagination Configuration (RFC-103) ==========

/**
 * Pagination configuration for workflow steps.
 * Enables automatic capture of multiple pages during workflow execution.
 */
export interface StepPaginationConfigDTO {
  enabled: boolean;

  /** Navigation method: 'url' for direct URL navigation, 'selector' for clicking next button */
  method: 'url' | 'selector';

  /** URL-based pagination configuration (preferred when available) */
  urlConfig?: PaginationUrlConfigDTO;

  /** Selector-based pagination configuration (fallback) */
  selectorConfig?: PaginationSelectorConfigDTO;

  /** Capture strategy */
  strategy: 'sample' | 'firstLast' | 'all';

  /** Strategy-specific configuration for 'sample' strategy */
  sampleConfig?: PaginationSampleConfigDTO;

  /** Strategy-specific configuration for 'firstLast' strategy */
  firstLastConfig?: PaginationFirstLastConfigDTO;

  /** Strategy-specific configuration for 'all' strategy */
  allConfig?: PaginationAllConfigDTO;
}

/** URL-based pagination configuration */
export interface PaginationUrlConfigDTO {
  /** URL pattern with {page} placeholder, e.g., "https://app.com/users?page={page}" */
  pattern: string;
  /** Starting page number (usually 1, sometimes 0) */
  startPage: number;
}

/** Selector-based pagination configuration */
export interface PaginationSelectorConfigDTO {
  /** CSS selector for the "next" button */
  nextSelector: string;
  /** Time to wait after clicking next (ms), default 1500 */
  waitAfterClick: number;
}

/** Sample strategy configuration */
export interface PaginationSampleConfigDTO {
  /** Hard limit on total pages to capture, default 10 */
  maxPages: number;
  /** Number of first pages to capture, default 2 */
  captureFirst: number;
  /** Number of last pages to capture, default 2 */
  captureLast: number;
  /** Number of evenly-spaced middle pages to sample, default 3 */
  sampleMiddle: number;
}

/** First/Last strategy configuration */
export interface PaginationFirstLastConfigDTO {
  /** Number of first pages to capture, default 1 */
  captureFirst: number;
  /** Number of last pages to capture, default 1 */
  captureLast: number;
  /** Safety cap on total pages, default 10 */
  maxPages: number;
}

/** All pages strategy configuration */
export interface PaginationAllConfigDTO {
  /** Safety cap on max pages, default 20 */
  maxPages: number;
}

// NEW: Define what to extract and where to store it
export interface StepExtractionDTO {
  name: string; // Output variable name (e.g., "issueTitle", "os")
  selector?: string; // Specific selector for this extraction (overrides step hints)
  xpath?: string;
  extractField?: ExtractField; // What property to extract
  description?: string; // What this extraction represents
}

export type StepAction =
  | 'navigate'
  | 'click'
  | 'type'
  | 'extract'
  | 'wait'
  | 'scroll'
  | 'hover'
  | 'select'
  | 'upload'
  | 'download'
  | 'screenshot';

export interface StepHintsDTO {
  // Selector strategies
  selector?: string;
  xpath?: string;
  testId?: string;
  ariaLabel?: string;

  // Element identification
  domText?: string;
  ordinality?: string; // "first" | "last" | "2" | "@position"
  containerSelector?: string; // NEW: Container constraint for performance

  // Validation
  fingerprint?: WorkflowElementFingerprintDTO;

  // Navigation hints
  url?: string;
  urlPattern?: string;

  // Type hints
  value?: string;
  clearFirst?: boolean;
  pressEnter?: boolean;

  // Extract hints
  extractField?: ExtractField;
  extractPattern?: string;
  extractAll?: boolean; // NEW: Extract from all matching elements (returns array)
  extractLimit?: number; // NEW: Limit number of items to extract (default: 50)
  extractTable?: boolean; // NEW: Extract table as structured data (2D array or objects)
  tableFormat?: 'array' | 'objects'; // NEW: Return format (default: 'objects')
  tableHeaderRow?: number; // NEW: Which row contains headers (default: 0)

  // Wait hints
  waitFor?: WaitConditionDTO;
  waitAfter?: WaitConditionDTO;
  timeout?: number;

  // Visual hints
  screenshot?: string;
  boundingBox?: BoundingBoxDTO;
}

export interface WorkflowElementFingerprintDTO {
  id?: string;
  href?: string;
  textContent?: string;
  tagName?: string;
  classList?: string[];
  attributes?: Record<string, string>;
}

export type ExtractField =
  | 'textContent'
  | 'value'
  | 'href'
  | 'src'
  | 'innerText'
  | 'innerHTML'
  | 'attribute';

export interface WaitConditionDTO {
  type: 'url' | 'element' | 'text' | 'networkIdle';
  value?: string;
  timeout?: number;
}

export interface BoundingBoxDTO {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface StepConditionDTO {
  type: 'if' | 'unless';
  check: ConditionCheckDTO;
  skipIfFalse?: boolean;
  errorIfFalse?: boolean;
}

export interface ConditionCheckDTO {
  type: 'elementExists' | 'elementNotExists' | 'urlMatches' | 'variableEquals';
  selector?: string;
  urlPattern?: string;
  variable?: string;
  value?: any;
}

export interface RetryConfigDTO {
  maxAttempts: number;
  delay?: number;
  backoff?: 'linear' | 'exponential';
}

// ========== Observed State ==========

export interface ObservedStateDTO {
  // Error elements detected during recording
  errorElements?: Array<{
    text: string;
    selector: string;
    color?: string;
    role?: string;
  }>;

  // Behavioral signals
  urlChanged?: boolean;
  modalAppeared?: boolean;
  httpStatus?: number;
}

export type StepValidationMode = 'auto' | 'dom' | 'visual' | 'hybrid';

export type StepIntentType =
  | 'focus_input'
  | 'open_panel'
  | 'close_panel'
  | 'toggle_on'
  | 'toggle_off'
  | 'submit_form'
  | 'navigate'
  | 'observe_only'
  | 'input_value'
  | 'select_option'
  | 'generic_click';

export type StepValidationStrictness = 'strict' | 'balanced' | 'lenient';
export type StepAuthFlow = 'none' | 'login' | 'logout' | 'switch_user' | 'unknown';
export type SessionExpiryCheckMode = 'auto' | 'strict' | 'off';
export type StepAuthFlowSource = 'llm' | 'heuristic' | 'manual';

export interface StepValidationPolicyDTO {
  mode?: StepValidationMode;
  intentType?: StepIntentType;
  authFlow?: StepAuthFlow;
  sessionExpiryCheck?: SessionExpiryCheckMode;
  authFlowSource?: StepAuthFlowSource;
  authFlowConfidence?: number;
  requiredSignals?: string[];
  optionalSignals?: string[];
  failureSignals?: string[];
  strictness?: StepValidationStrictness;
}

// ========== Execution Results ==========

export interface ExecutionResultDTO {
  workflowId: string;
  executedAt: string;
  params: Record<string, any>;
  outputs: Record<string, any>;

  results: StepResultDTO[];
  screenshots: string[];

  // Metrics
  totalCost: number;
  successRate: number;
  fastPathRate: number;
  executionTime: number;

  // Evidence pack
  evidencePack?: EvidencePackDTO;
}

export interface StepResultDTO {
  stepId: string;
  intent?: string; // Human-readable step name for UI display
  success: boolean;
  skipped?: boolean; // True if step was skipped due to previous step failure
  method: 'fast_path' | 'llm_adapted' | 'error' | 'recording' | 'observe' | 'vision' | 'llm' | 'native'; // Execution method / DeepGuide layer
  action?: StepAction; // Action type: click, navigate, type, extract, etc.

  // Fast path details
  selectorUsed?: string;
  fingerprintScore?: number;

  // Adaptive path details
  adaptedSelector?: string;
  confidence?: number;

  // Extraction
  extractedValue?: any; // Legacy: single extracted value
  extractedValues?: Record<string, any>; // NEW: Multiple extracted values by name
  actionCompleted?: boolean;

  // Timing
  startTime?: string;
  endTime?: string;
  duration?: number; // Step duration in milliseconds

  // RFC-087: Enhanced tracking fields
  screenshotUrl?: string; // Direct URL to screenshot in S3/R2

  // RFC-087: Self-healing selector tracking
  selectorHealed?: boolean; // Was selector automatically updated?
  originalSelector?: string; // Original recorded selector
  healedSelector?: string; // New selector found by observe/vision/llm
  healingMethod?: 'observe' | 'vision' | 'llm'; // Which layer healed it
  healingConfidence?: number; // 0-100 confidence score

  // Error handling
  error?: string;
  currentUrl?: string;     // URL where the step failed
  pageTitle?: string;      // Page title at failure time
  reason?: string;
  retries?: number;
}

export interface EvidencePackDTO {
  id: string;
  workflowId: string;
  execution: ExecutionResultDTO;
  attestation: AttestationDTO;
  generatedAt: string;
}

export interface AttestationDTO {
  signature: string;
  algorithm: string;
}

// ========== Browser Profile ==========

export interface BrowserProfileDTO {
  id: string;
  workspaceId: string;
  name: string;

  // Workflow-specific fields (optional)
  appName?: string;
  environment?: 'test' | 'production';

  // Compliance-specific fields (RFC-087)
  profileType?: string;  // Freeform: "Administrator", "Manager", "Auditor"
  email?: string;
  description?: string;

  loginUrl: string;

  // Storage (local or Steel)
  profilePath?: string;
  encryptionKeyId?: string;
  steelProfileId?: string;
  steelSessionId?: string;
  steelSessionUrl?: string;
  steelMetadata?: Record<string, unknown>;

  // Validation
  isValid: boolean;
  lastValidatedAt: string;
  lastValidatedBy?: string; // User ID if user-validated, null/undefined if system-validated
  validUntil?: string;
  userMarkedLoginIncomplete: boolean;

  // Timestamps
  createdAt: string;
  updatedAt: string;
  createdBy?: string;

  // Relations (included when requested)
  creator?: {
    id: string;
    name?: string;
    email?: string;
  };

  // Usage stats (included when requested)
  _count?: {
    scheduledExecutions?: number;
    executions?: number;
  };

  latestExecution?: {
    id: string;
    workflowId: string;
    status: string;
    executedAt: string;
    successRate?: number;
  };
}

// ========== Workflow Execution DTO ==========

export interface WorkflowExecutionDTO {
  id: string;
  workflowId: string;
  params: Record<string, any>;
  outputs: Record<string, any>;
  results: StepResultDTO[];
  screenshots: string[];
  totalCost: number;
  successRate: number;
  fastPathRate: number;
  executionTime: number;
  workflowValidation?: boolean; // Whether workflow-level assertions/outputs validation passed
  validationDetails?: {
    missingOutputs?: string[];
    failedAssertions?: string[];
    sessionExpiryVerdict?: {
      signalCount?: number;
      confirmedCount?: number;
      suppressedAuthSignalCount?: number;
      lastSignalStep?: number;
      lastAuthFlow?: StepAuthFlow;
    };
  };
  evidencePack?: EvidencePackDTO;
  executedAt: string;
  executedBy: string;
  failedType?: WorkflowExecutionFailedType;

  // RFC-087: Browser session replay (provider-agnostic)
  sessionProvider?: string; // 'steel' | 'browserbase' | 'playwright-cloud' | etc
  sessionId?: string;
  sessionUrl?: string;
  sessionMetadata?: Record<string, any>; // Optional: provider-specific data
}

// ========== Workflow Execution Progress ==========

export interface WorkflowExecutionProgressDTO {
  executionId: string;
  workflowId: string;
  status: 'initializing' | 'running' | 'completed' | 'failed' | 'cancelled';
  currentStep: number; // 0-indexed
  totalSteps: number;
  currentStepName?: string;
  currentStepStatus?: 'pending' | 'running' | 'completed' | 'failed';

  // Step results so far
  completedSteps: number;
  failedSteps: number;

  // Workflow validation status (only available at completion)
  workflowValidation?: boolean;
  validationDetails?: {
    missingOutputs?: string[];
    failedAssertions?: string[];
    sessionExpiryVerdict?: {
      signalCount?: number;
      confirmedCount?: number;
      suppressedAuthSignalCount?: number;
      lastSignalStep?: number;
      lastAuthFlow?: StepAuthFlow;
    };
  };
  successRate: number;

  // Timing
  startedAt: string;
  estimatedCompletion?: string;
  elapsedTime: number; // milliseconds

  // Latest screenshot (for live monitoring)
  latestScreenshot?: string;

  // Steel.dev session viewer URL (if using cloud browser)
  sessionViewerUrl?: string;

  // Steel session lifecycle
  sessionReleased?: boolean;
  sessionReleaseAt?: string;
  replayReady?: boolean;
  recordingUnavailable?: boolean;

  // Error details (if failed)
  error?: string;
}

// ========== Global State & Batch Execution (RFC-084) ==========

/// WorkflowExecutionContext - Persistent state across workflow executions
export interface WorkflowExecutionContextDTO {
  id: string;
  workspaceId: string;

  /// Context metadata
  name: string;
  description?: string;
  category: string; // "auth" | "compliance" | "bpo" | "testing" | "general"

  /// Global state (shared across executions)
  state: Record<string, any>;

  /// Security
  expiresAt?: string;
  isActive: boolean;

  /// Usage tracking
  usageCount: number;
  usedByWorkflows: string[];
  lastUsedAt: string;

  /// Audit trail
  createdAt: string;
  createdBy: string;
  updatedAt: string;
}

/// TestDataSet - Reusable test data for batch execution
export interface TestDataSetDTO {
  id: string;
  workspaceId: string;

  /// Metadata
  name: string;
  description?: string;
  category: string; // "compliance" | "bpo" | "qa" | "regression" | "testing"

  /// Data
  data: Record<string, any>[]; // Array of test records
  schema?: {
    fields: Array<{
      name: string;
      type: string;
      required?: boolean;
    }>;
  };

  /// Source information
  sourceType?: string; // "csv" | "api" | "manual" | "integration"
  sourceFile?: string;
  sourceUrl?: string;

  /// Versioning (for compliance audit trail)
  version: number;
  hash: string; // SHA-256 hash to detect tampering

  /// Compliance metadata
  controlId?: string; // SOC2/GDPR control (e.g., "CC6.1")
  auditPeriod?: string; // "Q1-2025"

  /// Statistics
  recordCount: number;
  executionCount: number;
  usedInWorkflows: string[];

  /// Audit trail
  createdAt: string;
  createdBy: string;
  updatedAt: string;
}

/// BatchExecution - Bulk workflow execution against test data set
export interface BatchExecutionDTO {
  id: string;
  workflowId: string;
  dataSetId: string;
  workspaceId: string;

  /// Configuration
  concurrency: number;
  headless: boolean;
  timeout?: number;
  contextId?: string; // WorkflowExecutionContext ID for shared state

  /// Status
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';

  /// Progress metrics
  totalRecords: number;
  processedRecords: number;
  successCount: number;
  failureCount: number;

  /// Results
  results?: {
    executions: Array<{
      recordId: number;
      executionId: string;
      status: 'success' | 'failed';
      duration: number;
      error?: string;
    }>;
    summary: {
      totalCost: number;
      avgDuration: number;
      fastPathRate: number;
    };
  };

  /// Timing
  startedAt?: string;
  completedAt?: string;
  duration?: number;

  /// Compliance metadata
  reviewedBy?: string;
  reviewedAt?: string;
  approved: boolean;

  /// Audit trail
  createdAt: string;
  createdBy: string;

  /// Relationships (optional)
  workflow?: {
    id: string;
    name: string;
  };
  dataSet?: {
    id: string;
    name: string;
    recordCount: number;
  };
}

/// BatchExecutionProgressDTO - Real-time progress update for batch execution
export interface BatchExecutionProgressDTO {
  batchId: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';

  /// Progress
  totalRecords: number;
  processedRecords: number;
  currentRecord?: number;

  /// Metrics
  successCount: number;
  failureCount: number;

  /// Timing
  startedAt: string;
  estimatedCompletion?: string;
  elapsedTime: number; // milliseconds

  /// Latest execution
  latestExecution?: {
    recordId: number;
    executionId: string;
    status: 'success' | 'failed';
  };

  /// Error details (if failed)
  error?: string;
}
